import type { BatchDeployResult } from '@/lib/types';
import { buildCsv } from '@/app/pages/assets/batch-entry/batch-utils';
import type { BatchLine, BatchLineOutcome } from '@/app/pages/assets/batch-entry/batch-line-utils';

export function deployOutcome(result: BatchDeployResult): BatchLineOutcome {
  if (result.status === 'deployed') {
    return { status: 'deployed', label: 'Sent', message: result.message, tone: 'success' };
  }
  if (result.status === 'failed') {
    return { status: 'failed', label: 'Failed', message: result.message, tone: 'error' };
  }
  const label = result.reason === 'not_found'
    ? 'Not found'
    : result.reason === 'duplicate'
      ? 'Duplicate'
      : 'Skipped';
  return {
    status: 'skipped',
    label,
    message: result.message,
    tone: result.reason === 'duplicate' ? 'warning' : 'error',
  };
}

export function buildSendGearCsv(batchId: string, batchName: string, projectName: string, lines: BatchLine[]): string {
  return buildCsv(
    ['batch_id', 'batch_name', 'job', 'serial', 'asset', 'category', 'status', 'message', 'asset_id'],
    lines.map((line) => [
      batchId,
      batchName,
      projectName,
      line.serial,
      line.resolved?.asset?.display_name ?? '',
      line.resolved?.asset?.type_display ?? '',
      line.outcome?.status ?? (line.resolved ? `not_sent_${line.resolved.status}` : 'not_sent'),
      line.outcome?.message ?? line.resolved?.message ?? '',
      line.resolved?.asset?.id ?? '',
    ])
  );
}

/**
 * Why "Send" cannot run right now, or null when it can. Shown in red next to
 * the button so a blocked send never fails silently. The serial entry boxes
 * are deliberately not part of this: once lines are in the batch the paste /
 * scan / range boxes may be empty on any tab.
 */
export function sendGearBlockReason({
  hasProject,
  batchName,
  readyCount,
  resolvingCount,
}: {
  hasProject: boolean;
  batchName: string;
  readyCount: number;
  resolvingCount: number;
}): string | null {
  if (!hasProject) return 'Pick the destination job first (step 1).';
  if (!batchName.trim()) return 'Enter a batch name first (step 1).';
  if (resolvingCount > 0) {
    return `Still looking up ${resolvingCount} serial${resolvingCount === 1 ? '' : 's'} — try again in a moment.`;
  }
  if (readyCount === 0) return 'Nothing is ready to send — every line in the list is blocked or not found.';
  return null;
}
